import { Link, useParams } from "react-router-dom";
import { ArrowRight, Bath, Grab, Accessibility } from "lucide-react";
import ChapterHeading from "@/components/ChapterHeading";
import ImageReveal from "@/components/ImageReveal";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { QuoteButton, CallButton } from "@/components/Buttons";
import usePageMeta from "@/hooks/usePageMeta";
import { SUBURBS_DETAILED } from "@/data/suburbs";

const SERVICE_LINKS = [
  {
    icon: Grab,
    title: "Grab Rails",
    copy: "Bathroom, shower, toilet and wall-mounted grab rails supplied and installed.",
    to: "/grab-rails",
    testid: "suburb-service-grab-rails",
  },
  {
    icon: Bath,
    title: "Hand Rails",
    copy: "Practical hand rails for stairs, entrances, pathways and steps.",
    to: "/hand-rails",
    testid: "suburb-service-hand-rails",
  },
  {
    icon: Accessibility,
    title: "Rubber Ramps",
    copy: "Rubber ramp solutions for suitable steps, thresholds and changes in level.",
    to: "/rubber-ramps",
    testid: "suburb-service-rubber-ramps",
  },
];

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = SUBURBS_DETAILED.find((s) => s.slug === slug);

  usePageMeta(
    suburb
      ? `Grab Rails ${suburb.name} | Hand Rails & Ramps | Modify`
      : "Service Areas | Modify Perth",
    suburb
      ? `Grab rails, hand rails and rubber ramps supplied and professionally installed across ${suburb.name}, WA. Local, careful installation. Free quotes from Modify.`
      : "Modify services suburbs across Perth, WA with grab rails, hand rails and rubber ramps."
  );

  if (!suburb) {
    return (
      <div className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-8" data-testid="suburb-not-found">
        <h1 className="font-display text-4xl font-extrabold text-charcoal sm:text-5xl">Area not found.</h1>
        <p className="mt-4 text-base text-ink/70">We service suburbs right across the Perth metro area.</p>
        <Link
          to="/contact"
          data-testid="suburb-not-found-contact-link"
          className="mt-8 inline-flex min-h-[48px] items-center rounded-full bg-ember px-7 py-3 text-sm font-bold tracking-[0.08em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
        >
          CONTACT US
        </Link>
      </div>
    );
  }

  const others = SUBURBS_DETAILED.filter((s) => s.slug !== suburb.slug);

  return (
    <div data-testid={`suburb-page-${suburb.slug}`}>
      <section className="relative overflow-hidden bg-ivory">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8 lg:pb-24 lg:pt-40">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal y={16}>
                <div className="flex items-center gap-4">
                  <span className="h-px w-10 bg-sage" aria-hidden="true" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">
                    Service Area — Perth, WA
                  </span>
                </div>
                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                  GRAB RAILS, HAND RAILS & RUBBER RAMPS IN {suburb.name.toUpperCase()}
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/75 sm:text-lg">{suburb.blurb}</p>
                {suburb.recent && (
                  <p className="mt-4 inline-block rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-sage-dark">
                    {suburb.recent}
                  </p>
                )}
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <QuoteButton testid={`suburb-${suburb.slug}-quote-button`} />
                  <CallButton />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <ImageReveal delay={0.15} radius="2rem" className="overflow-hidden rounded-[2rem] border border-line shadow-xl">
                <img
                  src={suburb.img}
                  alt={suburb.alt}
                  className="aspect-[4/3] w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </ImageReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <ChapterHeading
            num="01"
            eyebrow="Our services"
            title={`What we install in ${suburb.name}.`}
            testid={`suburb-${suburb.slug}-services`}
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_LINKS.map((service, i) => (
              <Reveal key={service.title} delay={i * 0.06}>
                <Link
                  to={service.to}
                  data-testid={service.testid}
                  className="group flex h-full flex-col rounded-3xl border border-line bg-ivory p-7 transition-colors duration-300 hover:border-sage"
                >
                  <service.icon className="h-7 w-7 text-sage-dark" aria-hidden="true" />
                  <h2 className="mt-4 font-display text-xl font-bold text-charcoal">{service.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{service.copy}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-[0.12em] text-charcoal transition-colors duration-200 group-hover:text-sage-dark">
                    LEARN MORE
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">
              Other areas we service
            </h2>
            <ul className="mt-5 flex flex-wrap gap-2">
              {others.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/areas/${s.slug}`}
                    data-testid={`suburb-nearby-${s.slug}`}
                    className="inline-flex min-h-[40px] items-center rounded-full border border-line bg-white px-4 text-xs font-bold text-ink/70 transition-colors duration-200 hover:border-sage hover:text-charcoal"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <CTABand
        title={`MAKE YOUR ${suburb.name.toUpperCase()} HOME SAFER TODAY.`}
        copy="Tell us what you need, send us a photo, and we'll help you find the right grab rail, hand rail or rubber ramp solution."
        testid={`suburb-${suburb.slug}-cta`}
      />
    </div>
  );
}
